// Daily Deposits / Sales By Payment Type — lifted out of the old
// SalesReportsTab so it can live under a dedicated URL and be linked
// from the Reports directory.
import React, { useEffect, useMemo, useState, useCallback } from "react";
// jspdf/xlsx exports are wired via ReportShell — see exportSpec below.
import { Link, useParams } from "react-router-dom";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, ArrowLeft, ExternalLink } from "lucide-react";
import ReportShell from "./ReportShell";
import DateRangeFilter, { useDateRange } from "./DateRangeFilter";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "Asia/Makassar", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function DailyDepositsReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [drillDetail, setDrillDetail] = useState(null);
  const { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range } = useDateRange("today");

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const r = await staffApi.get(`/reports/branch/${bid}/sales-by-payment`, { params: range });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [bid, range]);

  useEffect(() => { load(); }, [load]);

  // Flatten `methods` into Treatment + Deposit sub-rows per payment
  // type, matching the on-screen layout.
  const exportSpec = useMemo(() => {
    if (!data) return null;
    const money = (v) => formatBranchMoney(branch, v || 0);
    const num = { numeric: true, format: money, align: "right" };
    const rows = [];
    (data.methods || []).forEach((m) => {
      const t = m.treatment || {};
      const d = m.deposit   || {};
      if ((t.payments_count || 0) + (t.refunds_count || 0) > 0) {
        rows.push({
          payment_type: m.label,
          sub_type: "Treatment Sales",
          payments: Number(t.payments_total) || 0,
          refunds:  t.refunds_total ? -Math.abs(Number(t.refunds_total)) : 0,
          net:      Number(t.net_total) || 0,
        });
      }
      if ((d.payments_count || 0) + (d.refunds_count || 0) > 0) {
        rows.push({
          payment_type: (t.payments_count || t.refunds_count) ? "" : m.label,
          sub_type: "Deposit",
          payments: Number(d.payments_total) || 0,
          refunds:  d.refunds_total ? -Math.abs(Number(d.refunds_total)) : 0,
          net:      Number(d.net_total) || 0,
        });
      }
    });
    return {
      filename: "daily-deposits",
      range: { date_from: data.date_from, date_to: data.date_to },
      columns: [
        { header: "Payment Type", key: "payment_type" },
        { header: "Sub Type",     key: "sub_type" },
        { header: "Payments",     key: "payments", ...num },
        { header: "Refunds",      key: "refunds",  ...num },
        { header: "Total (Net)",  key: "net",      ...num },
      ],
      rows,
      // Three totals — matches the on-screen footer:
      // Total Treatment Sales / Total Deposits Held / Grand Total.
      footerRow: [
        "Totals", "",
        Number(data.total_payments) || 0,
        data.total_refunds ? -Math.abs(Number(data.total_refunds)) : 0,
        Number(data.grand_total ?? data.total_net) || 0,
      ],
    };
  }, [data, branch]);

  return (
    <ReportShell title="Daily Deposits / Sales By Payment Type" subtitle="Every cash, card and online payment collected inside the window — grouped by payout method." branchName={branch?.name} tab="sales" exportSpec={exportSpec}>
      <div className="space-y-4">
        <DateRangeFilter
          preset={preset} setPreset={setPreset}
          customFrom={customFrom} setCustomFrom={setCustomFrom}
          customTo={customTo} setCustomTo={setCustomTo}
          loading={loading} onRefresh={load}
        />

        {drillDetail ? (
          <DrillDown branch={branch} bid={bid} method={drillDetail} onBack={() => setDrillDetail(null)} />
        ) : (
          <Card className="rounded-lg overflow-hidden" data-testid="sales-by-payment-card">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border flex items-baseline justify-between">
                <div>
                  <p className="tracking-overline text-[10px] text-muted-foreground">Report</p>
                  <h2 className="text-lg font-semibold">Sales By Payment Type</h2>
                </div>
                {data && (
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Grand total</div>
                    <div className="text-xl font-semibold" data-testid="sales-grand-total">{formatBranchMoney(branch, data.grand_total ?? data.total_net)}</div>
                  </div>
                )}
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="uppercase text-[10px] tracking-wider w-40">Payment Type</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider">Sub Type</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider text-right w-40">Payments</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider text-right w-40">Refunds</TableHead>
                    <TableHead className="uppercase text-[10px] tracking-wider text-right w-44">Total (Net)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.methods || []).length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">
                      {loading ? "Loading…" : "No data available."}
                    </TableCell></TableRow>
                  ) : (data.methods).map((m) => {
                    // Iter-f11 — one method = up to two rows: Treatment
                    // Sales and Deposit. Methods with activity on both
                    // sub-rows render both; empty ones collapse to a
                    // single faded row so the layout stays regular.
                    const t = m.treatment || {};
                    const d = m.deposit   || {};
                    const tHas = (t.payments_count || 0) + (t.refunds_count || 0) > 0;
                    const dHas = (d.payments_count || 0) + (d.refunds_count || 0) > 0;
                    const anyActivity = tHas || dHas;
                    const rows = [];
                    rows.push({ kind: "treatment", label: "Treatment Sales", sub: t, has: tHas, isFirst: true });
                    rows.push({ kind: "deposit",   label: "Deposit",         sub: d, has: dHas, isFirst: false });
                    return (
                      <React.Fragment key={m.key}>
                        {rows.map((r, idx) => (
                          <TableRow
                            key={`${m.key}-${r.kind}`}
                            className={`border-b ${r.has ? "hover:bg-secondary/40" : "opacity-50"}`}
                            data-testid={`sales-row-${m.key}-${r.kind}`}
                          >
                            <TableCell className={`font-medium ${idx === 0 ? "" : "text-muted-foreground"}`}>
                              {r.isFirst ? m.label : ""}
                            </TableCell>
                            <TableCell className="text-sm">
                              <span className={r.kind === "deposit" ? "text-amber-800" : ""} data-testid={`sales-subtype-${m.key}-${r.kind}`}>
                                {r.label}
                              </span>
                            </TableCell>
                            <TableCell className="text-right text-sm">{formatBranchMoney(branch, r.sub.payments_total)}</TableCell>
                            <TableCell className="text-right text-sm">{r.sub.refunds_total ? `- ${formatBranchMoney(branch, r.sub.refunds_total)}` : formatBranchMoney(branch, 0)}</TableCell>
                            <TableCell className="text-right font-semibold">
                              <button
                                className={`inline-flex items-center gap-1 ${r.has ? "text-primary hover:underline" : ""}`}
                                onClick={() => r.has && setDrillDetail({ ...m, _kind: r.kind, _label: r.label, _sub: r.sub })}
                                disabled={!r.has}
                                data-testid={`sales-drill-${m.key}-${r.kind}`}
                              >
                                {formatBranchMoney(branch, r.sub.net_total)}
                                {r.has && <ExternalLink className="w-3 h-3" />}
                              </button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {data && (
                    <>
                      <TableRow className="bg-muted font-semibold" data-testid="sales-totals-treatment-row">
                        <TableCell colSpan={2}>Total Treatment Sales</TableCell>
                        <TableCell className="text-right">
                          {formatBranchMoney(branch, (data.methods || []).reduce((a, m) => a + (m.treatment?.payments_total || 0), 0))}
                        </TableCell>
                        <TableCell className="text-right">
                          {(() => {
                            const r = (data.methods || []).reduce((a, m) => a + (m.treatment?.refunds_total || 0), 0);
                            return r ? `- ${formatBranchMoney(branch, r)}` : formatBranchMoney(branch, 0);
                          })()}
                        </TableCell>
                        <TableCell className="text-right">{formatBranchMoney(branch, data.total_treatment_sales ?? 0)}</TableCell>
                      </TableRow>
                      <TableRow className="bg-amber-50 font-semibold" data-testid="sales-totals-deposits-row">
                        <TableCell colSpan={2}>Total Deposits Held</TableCell>
                        <TableCell className="text-right">
                          {formatBranchMoney(branch, (data.methods || []).reduce((a, m) => a + (m.deposit?.payments_total || 0), 0))}
                        </TableCell>
                        <TableCell className="text-right">
                          {(() => {
                            const r = (data.methods || []).reduce((a, m) => a + (m.deposit?.refunds_total || 0), 0);
                            return r ? `- ${formatBranchMoney(branch, r)}` : formatBranchMoney(branch, 0);
                          })()}
                        </TableCell>
                        <TableCell className="text-right">{formatBranchMoney(branch, data.total_deposits_held ?? 0)}</TableCell>
                      </TableRow>
                      <TableRow className="bg-primary/10 font-bold" data-testid="sales-totals-grand-row">
                        <TableCell colSpan={2}>Grand Total</TableCell>
                        <TableCell className="text-right">{formatBranchMoney(branch, data.total_payments)}</TableCell>
                        <TableCell className="text-right">{data.total_refunds ? `- ${formatBranchMoney(branch, data.total_refunds)}` : formatBranchMoney(branch, 0)}</TableCell>
                        <TableCell className="text-right">{formatBranchMoney(branch, data.grand_total ?? data.total_net)}</TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </ReportShell>
  );
}

function DrillDown({ branch, bid, method, onBack }) {
  // Iter-f11 — drill drops into either Treatment or Deposit sub-row
  // depending on which cell was clicked. The parent stores the active
  // sub via `_kind` / `_sub` on the method clone.
  const activeSub = method._sub || { transactions: [], payments_count: 0, refunds_count: 0, net_total: method.net_total };
  const kindLabel = method._label || "";
  const payments = (activeSub.transactions || []).filter((t) => t.kind === "payment");
  const refunds  = (activeSub.transactions || []).filter((t) => t.kind === "refund");
  return (
    <div className="space-y-3" data-testid={`drill-detail-${method.key}`}>
      <button className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" onClick={onBack} data-testid="drill-back">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Sales
      </button>
      <Card className="rounded-lg overflow-hidden">
        <CardContent className="p-0">
          <div className="p-4 border-b border-border">
            <p className="tracking-overline text-[10px] text-muted-foreground">Transactions detail</p>
            <h2 className="text-lg font-semibold" data-testid="drill-title">{method.label}{kindLabel ? ` · ${kindLabel}` : ""} · Net {formatBranchMoney(branch, activeSub.net_total)}</h2>
            <p className="text-xs text-muted-foreground mt-1">
              {activeSub.payments_count} payment{activeSub.payments_count === 1 ? "" : "s"} · {activeSub.refunds_count} refund{activeSub.refunds_count === 1 ? "" : "s"}
            </p>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="uppercase text-[10px] tracking-wider">When</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider">Client Name</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider">Sub Type</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider text-right">Amount</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider">Order</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...payments, ...refunds].map((t, i) => (
                <TableRow key={`${t.order_id}-${t.kind}-${i}`} data-testid={`drill-row-${i}`}>
                  <TableCell className="text-xs whitespace-nowrap">{fmtTime(t.at)}</TableCell>
                  <TableCell className="text-sm">
                    {t.customer_id ? (
                      <Link to={`/staff/brand/customers/${t.customer_id}`} className="text-primary hover:underline">{t.customer_name}</Link>
                    ) : t.customer_name}
                  </TableCell>
                  <TableCell className="text-xs">{t.sub_type}</TableCell>
                  <TableCell className={`text-right font-medium ${t.kind === "refund" ? "text-rose-700" : ""}`}>
                    {t.kind === "refund" ? "- " : ""}{formatBranchMoney(branch, t.amount)}
                  </TableCell>
                  <TableCell className="text-xs">
                    <Link to={`/staff/branch/${bid}/orders?order=${t.order_id}`} className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`drill-order-link-${i}`}>
                      #{t.order_number} <ExternalLink className="w-3 h-3" />
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
              {(payments.length + refunds.length) === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No transactions.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
